import {
  AttachMoney,
  Description,
  History as HistoryIcon,
  Info as InfoIcon,
  Percent,
} from '@mui/icons-material';
import { Box, Grid, Typography } from '@mui/material';
import React, { useMemo } from 'react';

import { FactoringExtension } from '../../../../../services/redux/documentProfileSlice.types';
import { formatDate, formatMoney } from '../../../../functions/utils';
import ExtensionsTooltip from './ExtensionsTooltip';
import InfoRow from './InfoRow';
import { DocumentConciliationRateHistory } from './types';

const formatRate = (rate: string | null): string | null => {
  if (!rate) return null;
  return `${rate}%`;
};

interface SummarySectionProps {
  operationDate: string | null;
  businessRate: string | null;
  documentAmount: number | null;
  interestCost: number | null;
  paymentDate: string | null;
  startingPaymentDate?: string | null;
  advance: number | null;
  morosityRate: number | null;
  netAmount: number | null;
  days: number | null;
  invoiceAmount: number | null;
  fundName: string | null;
  documentId?: string | null;
  rateHistory?: DocumentConciliationRateHistory | null;
  onHistoryLoaded?: (history: DocumentConciliationRateHistory) => void;
  extensions?: FactoringExtension[];
}

const SummarySection: React.FC<SummarySectionProps> = ({
  operationDate,
  businessRate,
  documentAmount,
  interestCost,
  paymentDate,
  startingPaymentDate: _startingPaymentDate,
  advance,
  morosityRate,
  netAmount,
  days,
  invoiceAmount,
  fundName,
  documentId,
  rateHistory,
  onHistoryLoaded,
  extensions = [],
}) => {
  // Calcular fecha final (última extensión o paymentDate)
  const finalPaymentDate = useMemo(() => {
    if (extensions && extensions.length > 0) {
      // Ordenar por dueDate descendente y tomar la última
      const sorted = [...extensions].sort((a, b) => {
        const dateA = new Date(a.dueDate).getTime();
        const dateB = new Date(b.dueDate).getTime();
        return dateB - dateA;
      });
      return formatDate(new Date(sorted[0].dueDate));
    }
    return paymentDate;
  }, [extensions, paymentDate]);

  const hasExtensions = extensions && extensions.length > 0;
  const hasMultipleExtensions = extensions && extensions.length > 1;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box
        sx={{
          backgroundColor: 'rgba(156, 39, 176, 0.08)',
          borderRadius: '8px',
          px: 1.5,
          py: 0.8,
          border: '1px solid rgba(156, 39, 176, 0.15)',
          mt: 1,
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            color: 'purple',
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            textAlign: 'center',
          }}
        >
          Resumen Operación
        </Typography>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <InfoRow
            label="Fecha Operación"
            value={operationDate}
            maxLength={40}
            icon={<Description sx={{ fontSize: '0.8rem', color: 'purple' }} />}
          />
          <InfoRow
            label="Tasa negocio"
            value={formatRate(businessRate)}
            maxLength={40}
            icon={<Percent sx={{ fontSize: '0.8rem', color: 'purple' }} />}
          />
          <InfoRow
            label="Tasa morosidad"
            value={formatRate(morosityRate?.toString() || null)}
            maxLength={40}
            icon={<Percent sx={{ fontSize: '0.8rem', color: 'purple' }} />}
            actionIcon={<HistoryIcon sx={{ fontSize: '1rem' }} />}
            actionTooltipText="Historial de tasas aplicadas"
            documentId={documentId || undefined}
            rateHistory={rateHistory}
            onHistoryLoaded={onHistoryLoaded}
          />
          <InfoRow
            label="Monto Original"
            value={invoiceAmount ? formatMoney(invoiceAmount) : null}
            maxLength={40}
            icon={<AttachMoney sx={{ fontSize: '0.8rem', color: 'purple' }} />}
          />
          <InfoRow
            label="Anticipo"
            value={formatRate(advance?.toString() || null)}
            maxLength={40}
            icon={<Percent sx={{ fontSize: '0.8rem', color: 'purple' }} />}
          />
          <InfoRow
            label="Monto Retenido"
            value={
              documentAmount && netAmount
                ? formatMoney(documentAmount - (netAmount || 0))
                : null
            }
            maxLength={40}
            icon={<AttachMoney sx={{ fontSize: '0.8rem', color: 'purple' }} />}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <InfoRow
            label="Fecha de vencimiento"
            value={finalPaymentDate}
            maxLength={40}
            icon={<Description sx={{ fontSize: '0.8rem', color: 'purple' }} />}
            actionIcon={
              hasExtensions ? (
                <InfoIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
              ) : undefined
            }
            actionTooltipText={
              hasExtensions
                ? hasMultipleExtensions
                  ? 'Ver todas las prórrogas'
                  : 'Ver detalles de la prórroga'
                : undefined
            }
            customTooltip={
              hasExtensions ? (
                <ExtensionsTooltip extensions={extensions} />
              ) : undefined
            }
          />
          <InfoRow
            label="Días"
            value={days?.toString() || null}
            maxLength={40}
            icon={<Description sx={{ fontSize: '0.8rem', color: 'purple' }} />}
          />
          <InfoRow
            label="Fondo"
            value={fundName || null}
            maxLength={40}
            icon={<Description sx={{ fontSize: '0.8rem', color: 'purple' }} />}
          />
          <InfoRow
            label="Monto Documento"
            value={documentAmount ? formatMoney(documentAmount) : null}
            maxLength={40}
            icon={<AttachMoney sx={{ fontSize: '0.8rem', color: 'purple' }} />}
          />
          <InfoRow
            label="Monto Anticipado"
            value={netAmount ? formatMoney(netAmount) : null}
            maxLength={40}
            icon={<AttachMoney sx={{ fontSize: '0.8rem', color: 'purple' }} />}
          />
          <InfoRow
            label="Costo de Interés"
            value={interestCost ? formatMoney(interestCost) : null}
            maxLength={40}
            icon={<AttachMoney sx={{ fontSize: '0.8rem', color: 'purple' }} />}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default SummarySection;
